import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Fade,
  Zoom,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SchoolIcon from "@mui/icons-material/School";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { motion } from "framer-motion";
import { addModule } from "../../State/Module/Action";

const Module = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { module } = useSelector((store) => store);
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('courseId');
  const [moduleName, setModuleName] = useState("");
  const [moduleDescription, setModuleDescription] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!courseId) {
      console.error("Course ID is required");
      return;
    }
    
    const data = new FormData(e.currentTarget);
    const moduleData = {
      name: data.get("moduleName"),
      description: data.get("moduleDescription"),
      courseId: courseId,
    };

    const reqData = { moduleData, navigate };
    dispatch(addModule(reqData));
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        p: 3,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><defs><pattern id=\"grain\" width=\"100\" height=\"100\" patternUnits=\"userSpaceOnUse\"><circle cx=\"25\" cy=\"25\" r=\"1\" fill=\"rgba(255,255,255,0.05)\"/><circle cx=\"75\" cy=\"75\" r=\"1\" fill=\"rgba(255,255,255,0.05)\"/></pattern></defs><rect width=\"100\" height=\"100\" fill=\"url(%23grain)\"/></svg>')",
          opacity: 0.3,
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <Card
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 500,
            p: 5,
            borderRadius: 4,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.2)",
            border: "1px solid rgba(255,255,255,0.3)",
          }}
        >
          <CardContent>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    mr: 2,
                    backgroundColor: "rgba(102, 126, 234, 0.1)",
                    color: "#667eea",
                    "&:hover": {
                      backgroundColor: "rgba(102, 126, 234, 0.2)",
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography
                  variant="h4"
                  sx={{ 
                    fontWeight: "700",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    display: "flex", 
                    alignItems: "center", 
                    gap: 2,
                    letterSpacing: "-0.02em",
                  }}
                >
                  <Box sx={{ 
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: 2,
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    <SchoolIcon sx={{ color: "white", fontSize: "1.5rem" }} />
                  </Box>
                  Add New Module
                </Typography>
              </Box>
            </motion.div>

            <form onSubmit={handleSubmit}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Box sx={{ my: 3 }}>
                <TextField
                label="Module Name"
                fullWidth
                name="moduleName"
                variant="outlined"
                value={moduleName}
                onChange={(e) => setModuleName(e.target.value)}
                inputProps={{
                  maxLength: 1000
                }}
                InputProps={{
                  startAdornment: <SchoolIcon sx={{ mr: 1, color: '#667eea' }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                    },
                    '& fieldset': {
                      borderColor: 'rgba(102, 126, 234, 0.3)',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: '600',
                    fontSize: '1rem',
                  },
                }}
                />
              </Box>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Box sx={{ my: 3 }}>
                <TextField
                label="Module Description"
                fullWidth
                multiline
                rows={4}
                name="moduleDescription"
                variant="outlined"
                value={moduleDescription}
                onChange={(e) => setModuleDescription(e.target.value)}
                helperText={`${moduleDescription.length}/1000 characters`}
                inputProps={{
                  maxLength: 1000
                }}
                InputProps={{
                  startAdornment: <DescriptionIcon sx={{ mr: 1, color: '#667eea' }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                    },
                    '& fieldset': {
                      borderColor: 'rgba(102, 126, 234, 0.3)',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: '600',
                    fontSize: '1rem',
                  },
                  '& .MuiFormHelperText-root': {
                    color: moduleDescription.length > 800 ? '#f59e0b' : 
                           moduleDescription.length > 900 ? '#ef4444' : '#64748b',
                    fontWeight: '500',
                    fontSize: '0.875rem',
                  },
                }}
                />
              </Box>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <Button
                type="submit"
                fullWidth
                variant="contained"
                endIcon={<NavigateNextIcon />}
                sx={{
                  mt: 4,
                  mb: 2,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 25px rgba(102, 126, 234, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Create Module
              </Button>
            </motion.div>

            {module?.error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  backgroundColor: 'rgba(239, 68, 68, 0.1)', 
                  borderRadius: 2,
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                }}>
                  <Typography 
                    color="error" 
                    sx={{ 
                      fontWeight: '500',
                      textAlign: 'center',
                    }}
                  >
                    {module.error}
                  </Typography>
                </Box>
              </motion.div>
            )}
          </form>
        </CardContent>
      </Card>
    </motion.div>
    </Box>
  );
};

export default Module;
